import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Printer } from 'lucide-react';

/**
 * Recap deck (build-v2 G2) — the 11-slide client recap, faithful to the
 * prototype `insights.jsx` RecapDeckModal. Built entirely from REAL measured
 * data (MENT / trial funnel / demographics / DiD / stakeholder / per-market),
 * navigable slide-by-slide, and printable (Save as PDF) via `window.print()`
 * with a print stylesheet that isolates the active slide.
 */
export interface RecapMarket {
  name: string;
  ment: number;
  responses: number;
  reach: number;
}
export interface RecapData {
  brandName: string;
  dateLabel: string;
  projects: number;
  events: number;
  reach: number;
  responses: number;
  ment: number;
  responseRate: number;
  funnel: { scanned: number; started: number; tried: number; enjoyed: number; shifted: number };
  gender: { label: string; pct: number }[];
  age: { label: string; pct: number }[];
  did: { treatedRate: number; controlRate: number; liftPp: number };
  stakeholder: { alignedPct: number; avgRating: number };
  byMarket: RecapMarket[];
}

export function RecapDeck({ data, onClose }: { data: RecapData; onClose: () => void }) {
  const [slide, setSlide] = useState(0);
  const slides = buildSlides(data);
  const go = (d: number) => setSlide((s) => Math.min(slides.length - 1, Math.max(0, s + d)));

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm">
      {/* print isolation: show only the active slide when printing */}
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #recap-print, #recap-print * { visibility: visible !important; }
        #recap-print { position: absolute; inset: 0; background: #fff; color: #111; }
        .recap-noprint { display: none !important; }
      }`}</style>

      <div className="recap-noprint flex items-center gap-3 border-b border-white/10 px-5 py-3">
        <span className="font-display text-sm font-extrabold text-white">{data.brandName} · Recap</span>
        <span className="mono rounded-full bg-[var(--color-xk-accent)]/20 px-2 py-0.5 text-[10px] font-bold text-[var(--color-xk-accent)]">
          11 slides
        </span>
        <button
          type="button"
          onClick={() => window.print()}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-bold text-white hover:border-[var(--color-xk-accent)]"
        >
          <Printer className="h-3.5 w-3.5" /> Export PDF
        </button>
        <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-white/70 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* slide rail */}
        <div className="recap-noprint hidden w-52 flex-none flex-col gap-0.5 overflow-y-auto border-r border-white/10 p-2 lg:flex">
          {slides.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSlide(i)}
              className={
                'flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs transition-colors ' +
                (i === slide ? 'bg-white/10 text-white' : 'text-white/55 hover:bg-white/5')
              }
            >
              <span className="mono text-[10px] text-[var(--color-xk-accent)]">{String(i + 1).padStart(2, '0')}</span>
              <span className="truncate">{s.title}</span>
            </button>
          ))}
        </div>

        {/* active slide */}
        <div className="flex flex-1 flex-col">
          <div id="recap-print" className="flex flex-1 items-center justify-center overflow-auto p-8">
            <div className="w-full max-w-3xl">{slides[slide].body}</div>
          </div>
          <div className="recap-noprint flex items-center gap-3 border-t border-white/10 px-5 py-3">
            <button
              type="button"
              onClick={() => go(-1)}
              disabled={slide === 0}
              className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <div className="flex flex-1 items-center justify-center gap-1.5">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSlide(i)}
                  className={'h-1.5 rounded-full transition-all ' + (i === slide ? 'w-5 bg-[var(--color-xk-accent)]' : 'w-1.5 bg-white/25')}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
            <span className="mono text-[11px] text-white/50">slide {String(slide + 1).padStart(2, '0')} / 11</span>
            <button
              type="button"
              onClick={() => go(1)}
              disabled={slide === slides.length - 1}
              className="flex items-center gap-1 rounded-lg bg-[var(--color-xk-accent)] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-30"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── slide building blocks (light theme, print-friendly) ──────────────── */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="mono text-xs font-bold uppercase tracking-[0.2em] text-[var(--color-xk-accent)]">{children}</div>;
}
function H({ children }: { children: React.ReactNode }) {
  return <h2 className="font-display mt-2 text-3xl font-extrabold tracking-tight text-white">{children}</h2>;
}
function Big({ value, label, tone }: { value: string; label: string; tone?: string }) {
  return (
    <div>
      <div className="font-display text-5xl font-extrabold" style={{ color: tone ?? '#fff' }}>{value}</div>
      <div className="mono mt-1 text-xs uppercase tracking-wider text-white/50">{label}</div>
    </div>
  );
}

function buildSlides(d: RecapData): { title: string; body: React.ReactNode }[] {
  const pct = (n: number) => (d.funnel.scanned ? Math.round((n / d.funnel.scanned) * 100) : 0);
  return [
    {
      title: 'Cover',
      body: (
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="h-16 w-16 rounded-full" style={{ background: 'radial-gradient(circle at 35% 30%, #d6ff5a, #b6e23a 70%)' }} />
          <Eyebrow>Activation recap · {d.dateLabel}</Eyebrow>
          <h1 className="font-display text-5xl font-extrabold tracking-tight text-white">{d.brandName}</h1>
          <p className="text-sm text-white/60">{d.projects} projects · {d.events} events · {d.reach.toLocaleString()} reached</p>
        </div>
      ),
    },
    {
      title: 'The headline',
      body: (
        <div className="flex flex-col gap-6">
          <Eyebrow>The headline</Eyebrow>
          <H>MENT {d.ment || '—'} / 10 across {d.events} events</H>
          <div className="grid grid-cols-3 gap-6">
            <Big value={d.reach.toLocaleString()} label="People reached" tone="#4dd28b" />
            <Big value={`${d.responseRate}%`} label="Response rate" tone="#5aa9ff" />
            <Big value={`${d.did.liftPp >= 0 ? '+' : ''}${d.did.liftPp}pp`} label="Preference lift" tone="var(--color-xk-accent)" />
          </div>
        </div>
      ),
    },
    {
      title: 'What we set out to do',
      body: (
        <div className="flex flex-col gap-4">
          <Eyebrow>What we set out to do</Eyebrow>
          <H>Drive trial and shift preference</H>
          <p className="text-base text-white/70">
            Put {d.brandName} in hands across {d.events} sampling events, validate the trial funnel with CS-1,
            and measure whether tasting moved preference — with venue stakeholders signing off on execution.
          </p>
        </div>
      ),
    },
    {
      title: 'What we did',
      body: (
        <div className="flex flex-col gap-6">
          <Eyebrow>What we did</Eyebrow>
          <H>{d.events} events · {d.reach.toLocaleString()} reached</H>
          <div className="grid grid-cols-3 gap-6">
            <Big value={d.projects.toLocaleString()} label="Markets" />
            <Big value={d.events.toLocaleString()} label="Events" />
            <Big value={d.responses.toLocaleString()} label="CS-1 responses" />
          </div>
        </div>
      ),
    },
    {
      title: 'It matched the plan',
      body: (
        <div className="flex flex-col gap-6">
          <Eyebrow>It matched the plan</Eyebrow>
          <H>{d.stakeholder.alignedPct}% of venues confirmed it matched the brief</H>
          <div className="grid grid-cols-2 gap-6">
            <Big value={`${d.stakeholder.alignedPct}%`} label="Stakeholder aligned" tone="#4dd28b" />
            <Big value={`${d.stakeholder.avgRating}/5`} label="Execution rating" />
          </div>
        </div>
      ),
    },
    {
      title: 'The story by market',
      body: (
        <div className="flex flex-col gap-4">
          <Eyebrow>The story by market</Eyebrow>
          <H>Performance across {d.byMarket.length} markets</H>
          <div className="flex flex-col gap-2">
            {d.byMarket.slice(0, 8).map((m) => (
              <div key={m.name} className="flex items-center gap-3">
                <span className="w-48 truncate text-sm text-white/80">{m.name}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-[var(--color-xk-accent)]" style={{ width: `${(m.ment / 10) * 100}%` }} />
                </div>
                <span className="mono w-24 text-right text-xs text-white/60">MENT {m.ment} · {m.responses.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'What consumers told us',
      body: (
        <div className="flex flex-col gap-4">
          <Eyebrow>What consumers told us</Eyebrow>
          <H>Trial-validation funnel</H>
          <div className="flex flex-col gap-2">
            {([
              ['QR scanned', d.funnel.scanned],
              ['Started', d.funnel.started],
              ['Tried', d.funnel.tried],
              ['Enjoyed', d.funnel.enjoyed],
              ['Pref. shift', d.funnel.shifted],
            ] as const).map(([label, n]) => (
              <div key={label} className="flex items-center gap-3">
                <span className="w-28 text-sm text-white/80">{label}</span>
                <div className="h-4 flex-1 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-[#4dd28b]" style={{ width: `${pct(n)}%` }} />
                </div>
                <span className="mono w-24 text-right text-xs text-white/60">{n.toLocaleString()} · {pct(n)}%</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'What it means for the brand',
      body: (
        <div className="flex flex-col gap-6">
          <Eyebrow>What it means for the brand</Eyebrow>
          <H>Tasting moved preference {d.did.liftPp >= 0 ? '+' : ''}{d.did.liftPp}pp</H>
          <div className="grid grid-cols-2 gap-6">
            <Big value={`${d.did.treatedRate}%`} label="Treated (opt-in)" tone="var(--color-xk-accent)" />
            <Big value={`${d.did.controlRate}%`} label="Control proxy" tone="#5aa9ff" />
          </div>
          <p className="text-sm text-white/60">
            Quasi-DiD: preference-shift among opt-in (treated) vs non-opt-in (control proxy), measured post-trial.
          </p>
        </div>
      ),
    },
    {
      title: 'The best and the worst',
      body: (
        <div className="flex flex-col gap-4">
          <Eyebrow>The best and the worst</Eyebrow>
          <H>Top and bottom markets by MENT</H>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="mono mb-2 text-xs uppercase tracking-wider text-[#4dd28b]">Best</div>
              {[...d.byMarket].sort((a, b) => b.ment - a.ment).slice(0, 3).map((m) => (
                <div key={m.name} className="flex justify-between py-1 text-sm text-white/80">
                  <span className="truncate">{m.name}</span><span className="mono text-[#4dd28b]">{m.ment}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="mono mb-2 text-xs uppercase tracking-wider text-[#ff5a4f]">Needs work</div>
              {[...d.byMarket].sort((a, b) => a.ment - b.ment).slice(0, 3).map((m) => (
                <div key={m.name} className="flex justify-between py-1 text-sm text-white/80">
                  <span className="truncate">{m.name}</span><span className="mono text-[#ff5a4f]">{m.ment}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'For next time',
      body: (
        <div className="flex flex-col gap-4">
          <Eyebrow>For next time</Eyebrow>
          <H>Recommendations</H>
          <ul className="flex flex-col gap-2 text-base text-white/75">
            <li>• Double down on the top {Math.min(3, d.byMarket.length)} markets — strongest MENT and trial conversion.</li>
            <li>• Lift the enjoyed→shift step ({pct(d.funnel.enjoyed)}%→{pct(d.funnel.shifted)}%) with a stronger TRY-anchor moment.</li>
            <li>• Keep stakeholder sign-off in the loop — {d.stakeholder.alignedPct}% alignment is a retention lever.</li>
          </ul>
        </div>
      ),
    },
    {
      title: 'The numbers in detail',
      body: (
        <div className="flex flex-col gap-4">
          <Eyebrow>The numbers in detail</Eyebrow>
          <H>Appendix</H>
          <div className="grid grid-cols-2 gap-x-10 gap-y-2 text-sm">
            {[
              ['Markets', d.projects.toLocaleString()],
              ['Events', d.events.toLocaleString()],
              ['Reach', d.reach.toLocaleString()],
              ['CS-1 responses', d.responses.toLocaleString()],
              ['MENT (avg)', String(d.ment)],
              ['Response rate', `${d.responseRate}%`],
              ['Pref. lift (DiD)', `${d.did.liftPp >= 0 ? '+' : ''}${d.did.liftPp}pp`],
              ['Stakeholder aligned', `${d.stakeholder.alignedPct}%`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/10 py-1.5">
                <span className="text-white/55">{k}</span>
                <span className="mono text-white">{v}</span>
              </div>
            ))}
          </div>
          <p className="mono text-[10px] text-white/40">
            Methodology terms appear in this appendix only. All slides above use plain language.
          </p>
        </div>
      ),
    },
  ];
}
